import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { format, isValid, parseISO } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import * as webhookLogService from "@/shared/api/services/webhookLogService";
import * as adminService from "@/shared/api/services/adminService";
import {
  Search, Webhook, ArrowDownLeft, ArrowUpRight, Loader2,
  Activity, CheckCircle2, XCircle, Clock, Zap, ChevronLeft, ChevronRight,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { auth } from "@/shared/api/graphqlClient";

/* ─── Status Config ──────────────────────────────────────────── */
const statusConfig = {
  received: { color: "text-cyan-400", bg: "rgba(34,211,238,0.1)", border: "rgba(34,211,238,0.25)", icon: Clock, label: "Received" },
  processed: { color: "text-emerald-400", bg: "rgba(52,211,153,0.1)", border: "rgba(52,211,153,0.25)", icon: CheckCircle2, label: "Processed" },
  failed: { color: "text-red-400", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)", icon: XCircle, label: "Failed" },
  invalid_signature: { color: "text-orange-400", bg: "rgba(251,146,60,0.1)", border: "rgba(251,146,60,0.25)", icon: XCircle, label: "Invalid Sig" },
};

/* ─── Stat Card ─────────────────────────────────────────────── */
function StatCard({ title, value, icon: Icon, accent, gradient, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 180, damping: 18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative"
      style={{ perspective: 900 }}
    >
      <motion.div
        animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1.06 : 0.85 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${accent}1a, transparent 68%)`, filter: "blur(8px)" }}
      />
      <motion.div
        animate={{ y: hovered ? -6 : 0, rotateX: hovered ? -1.5 : 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 24 }}
        className="relative rounded-2xl overflow-hidden p-5"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
          border: `1px solid ${hovered ? `${accent}35` : "rgba(148,163,184,0.1)"}`,
          boxShadow: hovered ? `0 18px 50px rgba(0,0,0,0.5), 0 0 28px ${accent}18` : "0 4px 18px rgba(0,0,0,0.3)",
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}50, transparent)` }} />
        <motion.div
          animate={{ x: hovered ? "108%" : "-108%" }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="absolute top-0 right-0 w-24 h-24 pointer-events-none" style={{ background: `radial-gradient(circle at 100% 0%, ${accent}08, transparent 70%)` }} />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
            <motion.p animate={{ scale: hovered ? 1.04 : 1 }} className="text-2xl font-bold text-slate-100">{value}</motion.p>
          </div>
          <motion.div
            animate={{ scale: hovered ? 1.12 : 1, rotate: hovered ? 6 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${gradient})`,
              boxShadow: hovered ? `0 0 22px ${accent}44` : `0 4px 14px ${accent}28`,
              transition: "box-shadow 0.3s",
            }}
          >
            <Icon className="h-5 w-5 text-white" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function EngineWebhooks() {

  const params = useParams();
  const engineId = params.engineId || auth.getUserData()?.userId;

  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);


  const { data: allResellers = [] } = useQuery({ queryKey: ["admin-resellers"], queryFn: () => adminService.getAllResellers() });
  const { data: allMerchants = [] } = useQuery({ queryKey: ["all-merchants"], queryFn: () => adminService.getAllMerchants() });

  const resellers = React.useMemo(() => {
    if (!engineId) return [];
    return allResellers.filter(r => r.createdByEngineId === engineId);
  }, [allResellers, engineId]);

  const merchants = React.useMemo(() => {
    if (!engineId) return [];
    const resellerIds = new Set(resellers.map(r => r.id));
    return allMerchants.filter(m => m.createdByEngineId === engineId || resellerIds.has(m.createdByResellerId));
  }, [allMerchants, resellers, engineId]);

  const merchantIds = React.useMemo(() => merchants.map(m => m.id), [merchants]);

  const { data: logsData, isLoading } = useQuery({
    queryKey: ["webhook-logs", merchantIds, debouncedSearch, page, limit],
    queryFn: () => webhookLogService.getWebhookLogs({ merchantIds, search: debouncedSearch, page, limit }),
    enabled: merchantIds.length > 0
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, merchantIds.join(",")]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const logs = React.useMemo(() => {
    return (logsData?.data || []).map(log => ({
      ...log,
      webhook_id: log.id,
      event_type: log.event,
      direction: "outbound",
      status: (log.http_status >= 200 && log.http_status < 300) ? "processed" : "failed",
      source: log.url,
      created_date: log.created_at,
      payload: log.request_payload
    }));
  }, [logsData]);

  const pagination = logsData || {};

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages || 1;
    const current = page;
    const start = Math.max(1, current - 2);
    const end = Math.min(total, current + 2);
    const nums = [];
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  }, [page, pagination?.totalPages]);

  const stats = useMemo(
    () => ({
      total: pagination?.total || 0,
      processed: logs.filter((l) => l.status === "processed").length,
      failed: logs.filter((l) => l.status === "failed").length,
    }),
    [logs, pagination?.total]
  );

  return (
    <EngineLayout currentPage="EngineWebhooks">
      <div className="space-y-6 w-full max-w-full">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
            border: "1px solid rgba(34,211,238,0.15)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.45), rgba(139,92,246,0.3), transparent)" }} />
          <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(34,211,238,0.07), transparent 70%)" }} />
          <div className="relative">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-400 mb-1">Monitoring</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-1">{t("webhookLogs")}</h1>
            <p className="text-sm text-slate-400">
              <span className="text-cyan-400 font-semibold">{logs.length}</span> {t("logs")}
            </p>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard title="Total Webhooks" value={stats.total} icon={Activity} gradient="#22d3ee, #0284c7" accent="#22d3ee" delay={0} />
          <StatCard title="Processed" value={stats.processed} icon={CheckCircle2} gradient="#34d399, #059669" accent="#34d399" delay={0.05} />
          <StatCard title="Failed" value={stats.failed} icon={XCircle} gradient="#f87171, #dc2626" accent="#f87171" delay={0.1} />
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`${t("Search PaymentId")}...`}
              className="w-full h-10 pl-9 pr-4 rounded-xl text-sm text-slate-200 placeholder:text-slate-600 outline-none"
              style={{
                background: "rgba(15,23,42,0.7)",
                border: "1px solid rgba(148,163,184,0.12)",
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
              onFocus={e => { e.target.style.borderColor = "rgba(34,211,238,0.3)"; e.target.style.boxShadow = "0 0 0 3px rgba(34,211,238,0.07)"; }}
              onBlur={e => { e.target.style.borderColor = "rgba(148,163,184,0.12)"; e.target.style.boxShadow = "none"; }}
            />
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
            border: "1px solid rgba(148,163,184,0.09)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.25), transparent)" }} />
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 560 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.08)", background: "rgba(2,6,23,0.4)" }}>
                  {["Webhook", "Event", "Direction", "Status", "Date"].map((h, i) => (
                    <th key={i} className="text-left px-4 py-3.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="wait">
                  {isLoading ? (
                    <tr><td colSpan={5} className="text-center py-14">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <Loader2 className="h-5 w-5 text-cyan-500 animate-spin" />
                        <span className="text-sm">{t("loading")}</span>
                      </div>
                    </td></tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-14">
                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center gap-3">
                          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.1)" }}>
                            <Webhook className="h-6 w-6 text-slate-600" />
                          </div>
                          <p className="text-slate-600 text-sm">{t("noLogs") || "No logs found"}</p>
                        </motion.div>
                      </td>
                    </tr>
                  ) : logs.map((log, i) => {
                    const statusKey = (log.status || "").toLowerCase().replace(/-/g, "_");
                    const st = statusConfig[statusKey] || statusConfig.received;
                    const StatusIcon = st.icon;
                    return (
                      <motion.tr
                        key={log.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ delay: i * 0.03 }}
                        onClick={() => setSelectedLog(log)}
                        style={{ borderBottom: "1px solid rgba(148,163,184,0.05)", cursor: "pointer", transition: "background 0.18s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        {/* Webhook ID */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            <motion.div
                              whileHover={{ scale: 1.1, rotate: 5 }}
                              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{
                                background: log.direction === "inbound"
                                  ? "linear-gradient(135deg, rgba(34,211,238,0.2), rgba(2,132,199,0.1))"
                                  : "linear-gradient(135deg, rgba(52,211,153,0.2), rgba(5,150,105,0.1))",
                                border: log.direction === "inbound"
                                  ? "1px solid rgba(34,211,238,0.2)"
                                  : "1px solid rgba(52,211,153,0.2)",
                                transition: "box-shadow 0.25s",
                              }}
                              onMouseEnter={e => e.currentTarget.style.boxShadow = log.direction === "inbound" ? "0 0 14px rgba(34,211,238,0.3)" : "0 0 14px rgba(52,211,153,0.3)"}
                              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                            >
                              {log.direction === "inbound"
                                ? <ArrowDownLeft className="h-4 w-4 text-cyan-400" />
                                : <ArrowUpRight className="h-4 w-4 text-emerald-400" />}
                            </motion.div>
                            <div>
                              <p className="font-mono text-sm text-slate-200">{log.webhook_id?.slice(0, 14)}...</p>
                              <p className="text-xs text-slate-500">{log.source}</p>
                            </div>
                          </div>
                        </td>

                        {/* Event */}
                        <td className="px-4 py-3.5">
                          <span className="font-mono text-xs text-slate-400 px-2.5 py-1 rounded-lg" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.12)" }}>
                            {log.event_type?.replace(/\./g, " ") || "—"}
                          </span>
                        </td>

                        {/* Direction */}
                        <td className="px-4 py-3.5">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold"
                            style={{
                              background: log.direction === "inbound" ? "rgba(34,211,238,0.08)" : "rgba(52,211,153,0.08)",
                              border: `1px solid ${log.direction === "inbound" ? "rgba(34,211,238,0.2)" : "rgba(52,211,153,0.2)"}`,
                            }}
                          >
                            {log.direction === "inbound"
                              ? <><ArrowDownLeft className="h-3 w-3 text-cyan-400" /><span className="text-cyan-400 capitalize">{t("inbound") || "Inbound"}</span></>
                              : <><ArrowUpRight className="h-3 w-3 text-emerald-400" /><span className="text-emerald-400 capitalize">{t("outbound") || "Outbound"}</span></>}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3.5">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold"
                            style={{ background: st.bg, border: `1px solid ${st.border}` }}>
                            <StatusIcon className={`h-3 w-3 ${st.color}`} />
                            <span className={st.color}>{st.label}</span>
                          </div>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3.5 text-xs text-slate-500">
                          {log.created_date ? format(new Date((Number(log.created_date))), "MMM d, yyyy HH:mm") : "N/A"}
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
            <style>{`
              .wh-pag { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-top: 16px; }
              .wh-pag-info { color: #94a3b8; font-size: 13px; font-weight: 500; }
              .wh-arr { width: 34px; height: 34px; border-radius: 10px; background: rgba(36,52,74,0.45); border: 1px solid rgba(148,163,184,0.25); display: flex; align-items: center; justify-content: center; color: #cbd5e1; cursor: pointer; transition: all 0.2s ease; }
              .wh-arr:hover:not(:disabled) { background: rgba(34,211,238,0.14); border-color: rgba(34,211,238,0.32); color: #22d3ee; transform: scale(1.05); }
              .wh-arr:disabled { opacity: 0.35; cursor: not-allowed; }
              .wh-pg { min-width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid transparent; color: #cbd5e1; transition: all 0.2s ease; }
              .wh-pg:hover { background: rgba(34,211,238,0.1); color: #22d3ee; }
              .wh-pg.on { background: linear-gradient(135deg, rgba(14,165,233,0.2), rgba(34,211,238,0.1)); border-color: rgba(34,211,238,0.3); color: #22d3ee; }
            `}</style>

            {pagination.totalPages > 1 && (
              <motion.div className="wh-pag" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                <div className="wh-pag-info">
                  Showing {pagination.total ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, pagination.total || 0)} of {pagination.total || 0}
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 text-sm outline-none"
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    className="wh-arr"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {pageNumbers.map((num) => (
                    <button
                      key={num}
                      className={`wh-pg${num === page ? " on" : ""}`}
                      onClick={() => setPage(num)}
                    >
                      {num}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages || 1))}
                    disabled={page >= (pagination.totalPages || 1)}
                    className="wh-arr"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {pagination.totalPages <= 1 && (
              <div className="wh-pag" style={{ justifyContent: "space-between" }}>
                <div className="wh-pag-info">
                  Showing {pagination.total ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, pagination.total || 0)} of {pagination.total || 0}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 text-sm outline-none"
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <SheetContent
          className="w-full sm:max-w-lg overflow-y-auto"
          style={{ background: "rgba(2,6,23,0.98)", borderLeft: "1px solid rgba(34,211,238,0.14)" }}
        >
          {selectedLog && (
            <>
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.35), transparent)" }} />
              <SheetHeader className="pb-4" style={{ borderBottom: "1px solid rgba(148,163,184,0.08)" }}>
                <SheetTitle className="flex items-center gap-3">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.2), rgba(2,132,199,0.1))", border: "1px solid rgba(34,211,238,0.2)" }}
                  >
                    <Webhook className="h-5 w-5 text-cyan-400" />
                  </motion.div>
                  <div>
                    <p className="text-lg text-slate-100">{t("webhookDetails") || "Webhook Details"}</p>
                    {(() => {
                      const sk = (selectedLog.status || "").toLowerCase().replace(/-/g, "_");
                      const sc = statusConfig[sk] || statusConfig.received;
                      return (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-bold mt-1"
                          style={{ background: sc.bg, border: `1px solid ${sc.border}` }}>
                          <span className={sc.color}>{sc.label}</span>
                        </div>
                      );
                    })()}
                  </div>
                </SheetTitle>
              </SheetHeader>

              <div className="py-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Direction", value: selectedLog.direction || "—" },
                    { label: "Event Type", value: selectedLog.event_type || "—" },
                    { label: "Source", value: selectedLog.source || "—" },
                    { label: "Date", value: selectedLog.created_date ? format(new Date(Number(selectedLog.created_date)), "MMM d, yyyy HH:mm") : "N/A" },
                  ].map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="p-3 rounded-xl"
                      style={{ background: "rgba(15,23,42,0.7)", border: "1px solid rgba(148,163,184,0.08)" }}
                    >
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{item.label}</p>
                      <p className="text-sm font-semibold text-slate-200 capitalize truncate">{item.value}</p>
                    </motion.div>
                  ))}
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Webhook ID</p>
                  <div className="px-3 py-2.5 rounded-xl font-mono text-xs text-cyan-400 break-all" style={{ background: "rgba(2,6,23,0.8)", border: "1px solid rgba(34,211,238,0.12)" }}>
                    {selectedLog.webhook_id}
                  </div>
                </div>

                {selectedLog.payload && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{t("payload") || "Payload"}</p>
                    <pre className="p-4 rounded-xl text-xs overflow-x-auto max-h-60 text-emerald-400" style={{ background: "rgba(2,6,23,0.9)", border: "1px solid rgba(52,211,153,0.12)" }}>
                      {typeof selectedLog.request_payload === "string"
                        ? selectedLog.request_payload
                        : JSON.stringify(selectedLog.request_payload, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.response_body && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Response Body</p>
                    <pre className="p-4 rounded-xl text-xs overflow-x-auto max-h-60 text-cyan-400" style={{ background: "rgba(2,6,23,0.9)", border: "1px solid rgba(34,211,238,0.12)" }}>
                      {typeof selectedLog.response_body === "string"
                        ? selectedLog.response_body
                        : JSON.stringify(selectedLog.response_body, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.error_message && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-red-400 mb-2">Error Message</p>
                    <div className="p-4 rounded-xl text-xs text-red-400 break-words" style={{ background: "rgba(248,113,113,0.05)", border: "1px solid rgba(248,113,113,0.15)" }}>
                      {selectedLog.error_message}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </EngineLayout>
  );
}